import { useState, useEffect } from 'react';

interface NavbarProps {
  cartCount?: number;
  onCartOpen?: () => void;
}

export default function Navbar({ cartCount = 0, onCartOpen }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center bg-red-600 rounded-lg">
                <i className="ri-music-2-fill text-xl text-white"></i>
              </div>
              <span className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                NewClazzMusicGroup
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a
                href="#beats"
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-red-600' : 'text-white/90 hover:text-white'
                }`}
              >
                Beats
              </a>
              <a
                href="#services"
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-red-600' : 'text-white/90 hover:text-white'
                }`}
              >
                Services
              </a>
              <a
                href="/licensing"
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-red-600' : 'text-white/90 hover:text-white'
                }`}
              >
                Licensing
              </a>
              <a
                href="#booking"
                className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-white text-gray-900 hover:bg-gray-100'
                }`}
              >
                Book Studio
              </a>

              {/* Cart Button */}
              <button
                onClick={onCartOpen}
                className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-white/15 text-white hover:bg-white/25'
                }`}
              >
                <i className="ri-shopping-cart-2-line text-lg"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-red-600 text-white text-xs font-bold rounded-full">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>
            </div>

            <div className="flex items-center gap-3 md:hidden">
              {/* Mobile Cart Button */}
              <button
                onClick={onCartOpen}
                className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                  isScrolled ? 'bg-gray-100 text-gray-700' : 'bg-white/15 text-white'
                }`}
              >
                <i className="ri-shopping-cart-2-line text-lg"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-red-600 text-white text-xs font-bold rounded-full">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                  isScrolled ? 'bg-gray-100 text-gray-900' : 'bg-white/10 text-white'
                }`}
              >
                <i className={`text-xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-20 bg-white pt-20 px-6 md:hidden">
          <div className="flex flex-col gap-6">
            <a
              href="#beats"
              onClick={() => setIsMenuOpen(false)}
              className="text-lg font-medium text-gray-900 hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Beats
            </a>
            <a
              href="#services"
              onClick={() => setIsMenuOpen(false)}
              className="text-lg font-medium text-gray-900 hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Services
            </a>
            <a
              href="/licensing"
              onClick={() => setIsMenuOpen(false)}
              className="text-lg font-medium text-gray-900 hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Licensing
            </a>
            <a
              href="#booking"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Book Studio
            </a>
          </div>
        </div>
      )}
    </>
  );
}
